import React, { ChangeEventHandler, useContext, useEffect, useState } from 'react'
import { CardContainer, ContentContainer, MainContainer } from '../../focus_view_shared.styles'
import { CardHeader, Text, Alert } from '@amzn/storm-ui'
import {
  AlertContainer,
  BoldText,
  EmailFieldEntryContainer,
  FormFieldNameContainer,
  FormFieldValueContainer,
  FormRowContainer,
  InviteDataEntryContainer,
  StyledCard,
  TextAreaFormGroupStyled,
} from '../bulk_users_invite.styles'
import { UserInviteRoleInfo } from '../bulk_users_invite_shared'
import { useTranslation } from 'react-i18next'
import { pipe } from 'fp-ts/es6/function'
import { InvalidEntry, parseListOfInvitees } from '../bulk_invite_logic'
import * as i18nKeyList from '../bulk_users_invite_i18n_mapping'
import * as AACChromeAPI from '@amzn/aac-chrome-api'
import { getUserManagementURL } from '../../ads_console_utils'
import { useLoginContext } from 'stagedive/LoginContext'
import { toNullable } from 'fp-ts/es6/Option'
import { StageDiveConfig, StageDiveConfigContext } from '../../../../../../util/app_initialization/appconfig'

interface UsersInviteDefectsProps {
  invalidEntries: InvalidEntry[]
  defectFixupInvitees: string[]
  setDefectFixupInvitees: (invitees: string[]) => void
  fixupAction: () => void
  skipAction: () => void
}

export const UsersInviteDefects: React.FunctionComponent<UsersInviteDefectsProps> = (props: UsersInviteDefectsProps): JSX.Element => {
  const [textAreaContent, setTextAreaContent] = useState<string>('')
  const { t } = useTranslation()
  const { loginContext } = useLoginContext()
  const partnerId = toNullable(loginContext.partnerProfileId) ?? ''
  const stageDiveConfig = useContext<StageDiveConfig>(StageDiveConfigContext)

  useEffect(() => {
    setTextAreaContent(props.invalidEntries.map((e) => e.value).join(',\n'))
  }, [props.invalidEntries])

  const handleBlur = (): void => {
    pipe(textAreaContent, parseListOfInvitees, props.setDefectFixupInvitees)
  }

  const handleTextAreaContentChange: ChangeEventHandler<HTMLTextAreaElement> = (event: React.ChangeEvent<HTMLTextAreaElement>) =>
    setTextAreaContent(event.target.value)

  useEffect(() => {
    const chromeConfig = AACChromeAPI.getChromeConfiguration()
    const pageHeader = new AACChromeAPI.PageHeader()
    const objectInfo = new AACChromeAPI.ObjectInfo()
    const actionList = new AACChromeAPI.ItemList()

    chromeConfig.setPageLayoutType('FOCUS')
    const focusModeExitEventId = 'ExitFocusView'

    const continueButton: AACChromeAPI.BUTTON = new AACChromeAPI.Button()

    const continueButtonId = 'continue'

    continueButton.setId(continueButtonId)
    continueButton.setLabel(t(i18nKeyList.BULK_INVITE_USERS_CONTINUE))
    continueButton.setButtonType(AACChromeAPI.ButtonType.NORMAL)
    continueButton.setState(textAreaContent.length === 0 ? AACChromeAPI.ActionState.DISABLED : AACChromeAPI.ActionState.ENABLED)
    const continueButtonClickFn = (): void => props.fixupAction()
    AACChromeAPI.getActionEventTarget().addEventListener(continueButtonId, continueButtonClickFn)

    const skipLink = new AACChromeAPI.Button()
    const skipLinkButtonId = 'skip'
    skipLink.setId(skipLinkButtonId)
    skipLink.setLabel(t(i18nKeyList.BULK_INVITE_USERS_SKIP))
    skipLink.setButtonType(AACChromeAPI.ButtonType.LINK)

    const skipLinkFn = (): void => props.skipAction()
    AACChromeAPI.getActionEventTarget().addEventListener(skipLinkButtonId, skipLinkFn)
    const backToUsersLinkFn = (): void => {
      window.location.href = getUserManagementURL(partnerId, stageDiveConfig.hostname)
    }
    AACChromeAPI.getCommonEventTarget().addEventListener(focusModeExitEventId, backToUsersLinkFn)

    actionList.addItem(skipLink)
    actionList.addItem(continueButton)

    objectInfo.setActions(actionList)

    pageHeader.setObjectInfo(objectInfo)
    pageHeader.setPageName(t(i18nKeyList.BULK_INVITE_USERS_PAGE_TITLE))
    chromeConfig.setPageHeader(pageHeader)
    AACChromeAPI.setChromeConfiguration(chromeConfig)
    return () => {
      AACChromeAPI.getActionEventTarget().removeEventListener(continueButtonId, continueButtonClickFn)
      AACChromeAPI.getActionEventTarget().removeEventListener(skipLinkButtonId, skipLinkFn)
      AACChromeAPI.getCommonEventTarget().removeEventListener(focusModeExitEventId, backToUsersLinkFn)
    }
  }, [textAreaContent, props.defectFixupInvitees, t])

  return (
    <MainContainer>
      <ContentContainer>
        <CardContainer>
          <StyledCard>
            <CardHeader>
              <Text textColor="base" type="h2">
                {t(i18nKeyList.BULK_INVITE_USERS_CARD_DESCRIPTION)}
              </Text>
            </CardHeader>
            <AlertContainer>
              <Alert type={'warning'} header={t(i18nKeyList.BULK_INVITE_USERS_DEFECT_ALERT_HEADER, { defectsCount: props.invalidEntries.length })}>
                {t(i18nKeyList.BULK_INVITE_USERS_DEFECT_ALERT_TEXT)}
              </Alert>
            </AlertContainer>
            <InviteDataEntryContainer>
              <FormRowContainer>
                <FormFieldNameContainer>
                  <BoldText>{t(i18nKeyList.BULK_INVITE_USERS_EMAIL_LABEL)}</BoldText>
                </FormFieldNameContainer>
                <FormFieldValueContainer>
                  <EmailFieldEntryContainer>
                    <Text type="p">{t(i18nKeyList.BULK_INVITE_USERS_EMAIL_TEXT_BOX_DESCRIPTION)}</Text>
                    <TextAreaFormGroupStyled
                      label={''}
                      aria-label={t(i18nKeyList.BULK_INVITE_USERS_DEFECTS_ARIA_LABEL)}
                      id={'defect-fix-entry'}
                      onBlur={handleBlur}
                      onChange={handleTextAreaContentChange}
                      value={textAreaContent}
                      rows={10}
                    />
                  </EmailFieldEntryContainer>
                </FormFieldValueContainer>
              </FormRowContainer>
              <UserInviteRoleInfo />
            </InviteDataEntryContainer>
          </StyledCard>
        </CardContainer>
      </ContentContainer>
    </MainContainer>
  )
}
